import type { ReactNode } from 'react';
import { useRef } from 'react';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Box, ButtonBase, Popover, Tooltip } from '@mui/material';

interface SplitMediaButtonProps {
  icon: ReactNode;
  inactiveIcon: ReactNode;
  isActive: boolean;
  onToggle: () => void;
  dropdownContent: ReactNode;
  isDropdownOpen: boolean;
  onDropdownOpenChange: (open: boolean) => void;
  ariaLabel: string;
  tooltip: string;
  popoverContainer?: HTMLElement | null;
}

export function SplitMediaButton({
  icon,
  inactiveIcon,
  isActive,
  onToggle,
  dropdownContent,
  isDropdownOpen,
  onDropdownOpenChange,
  ariaLabel,
  tooltip,
  popoverContainer,
}: SplitMediaButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Box ref={containerRef} sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {/* Main toggle */}
      <Tooltip title={tooltip}>
        <ButtonBase
          onClick={onToggle}
          aria-label={ariaLabel}
          sx={{
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            p: 0,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
          }}
        >
          {isActive ? icon : inactiveIcon}
        </ButtonBase>
      </Tooltip>

      {/* Chevron dropdown trigger — inline next to main button */}
      <ButtonBase
        onClick={(e) => {
          e.stopPropagation();
          onDropdownOpenChange(!isDropdownOpen);
        }}
        aria-label={`${ariaLabel} options`}
        sx={{
          width: 20,
          height: 48,
          minWidth: 0,
          p: 0,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
        }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: isActive ? 'grey.400' : 'error.main',
            transform: isDropdownOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </ButtonBase>

      {/* Dropdown popover */}
      <Popover
        open={isDropdownOpen}
        anchorEl={containerRef.current}
        onClose={() => onDropdownOpenChange(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        container={popoverContainer ?? undefined}
        slotProps={{ paper: { sx: { width: 300, mb: 1 } } }}
      >
        {dropdownContent}
      </Popover>
    </Box>
  );
}
